import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Award,
  Clock,
  CheckCircle,
  Users,
  BarChart3,
  PieChart,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader, MetricCard, DataTable, Column, KPIStrip } from '@/components/common';
import { toast } from 'sonner';
import { rfqApi, RFQAnalyticsData, RFQAnalyticsFilter } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { stagger, fadeUp } from '@/lib/animations';
import { formatCurrency as formatMoney, formatMoneyTotal } from '@/lib/utils';

type DateRangeOption = NonNullable<RFQAnalyticsFilter['dateRange']>;

export default function FreightRFQAnalytics() {
  const [dateRange, setDateRange] = useState<DateRangeOption>('6m');
  const [data, setData] = useState<RFQAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const analyticsData = await rfqApi.getRFQAnalytics({ dateRange });
      setData(analyticsData);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  /**
   * Rates and lane values on this screen carry their own currency; this used to
   * stamp USD on all of them. Kept as a prop the tabs receive so each call site
   * has to say what currency it is printing.
   */
  const formatCurrency = (value: number, currency = data?.summary.currency ?? 'USD') =>
    formatMoney(value, currency);

  if (loading) {
    return (
      <div className="space-y-5">
        <PageHeader
          title="RFQ Analytics"
          description="Carrier response and award rates by lane, with the total value quoted against each."
        />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-5">
        <PageHeader
          title="RFQ Analytics"
          description="Insights and performance metrics for freight procurement"
        />
        <div className="flex flex-col items-center justify-center h-64">
          <AlertTriangle className="w-12 h-12 text-warning-600 mb-4" />
          <h3 className="text-base font-semibold mb-2">Failed to Load Analytics</h3>
          <p className="text-muted-foreground mb-4">{error || 'Unable to fetch analytics data'}</p>
          <Button onClick={loadAnalytics}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="RFQ Analytics"
          description="Insights and performance metrics for freight procurement"
          actions={
            <div className="flex items-center gap-3">
              <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRangeOption)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1m">Last Month</SelectItem>
                  <SelectItem value="3m">Last 3 Months</SelectItem>
                  <SelectItem value="6m">Last 6 Months</SelectItem>
                  <SelectItem value="1y">Last Year</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* Summary KPIs */}
      <div>
        <KPIStrip columns={4}>
          <MetricCard
            label="Open RFQs"
            value={data.summary.openRFQs}
            sentiment="warning"
            icon={<Clock className="w-5 h-5 text-warning-600" />}
            trend={{ direction: 'neutral', value: `${data.summary.totalRFQs} total` }}
          />
          <MetricCard
            label="Awarded RFQs"
            value={data.summary.awardedRFQs}
            sentiment="success"
            icon={<Award className="w-5 h-5 text-success-500" />}
          />
          <MetricCard
            label="Total Value"
            value={formatMoneyTotal(
              data.summary.totalValue,
              data.summary.currency,
              data.summary.valueByCurrency
            )}
            sentiment="neutral"
            icon={<DollarSign className="w-5 h-5" />}
          />
          <MetricCard
            label="Average Savings"
            value={
              data.summary.avgSavingsPercent != null
                ? `${data.summary.avgSavingsPercent}%`
                : 'Not calculated'
            }
            sentiment="success"
            icon={<TrendingDown className="w-5 h-5 text-success-500" />}
            trend={{ direction: 'neutral', value: `${data.summary.avgTimeToAward} days to award` }}
          />
        </KPIStrip>
      </div>

      <motion.div variants={fadeUp}>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="vendors">Vendor Analysis</TabsTrigger>
            <TabsTrigger value="lanes">Lane Analysis</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab data={data} />
          </TabsContent>
          <TabsContent value="vendors" className="mt-6">
            <VendorsTab data={data} />
          </TabsContent>
          <TabsContent value="lanes" className="mt-6">
            <LanesTab data={data} formatCurrency={formatCurrency} />
          </TabsContent>
          <TabsContent value="trends" className="mt-6">
            <TrendsTab data={data} />
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}

function OverviewTab({ data }: { data: RFQAnalyticsData }) {
  const totalCount = data.statusBreakdown.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="space-y-8">
      {/* Status Breakdown */}
      <div className={cn(PANEL, 'p-6')}>
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
          <PieChart className="w-5 h-5 text-muted-foreground" />
          RFQ Status Distribution
        </h3>
        <div className="grid grid-cols-4 gap-4">
          {data.statusBreakdown.map((status) => {
            const percentage = ((status.count / totalCount) * 100).toFixed(1);
            const colors: Record<string, string> = {
              Draft: 'bg-muted border-muted-foreground/20',
              Open: 'bg-primary-50 border-primary-200',
              Closed: 'bg-warning-50 border-warning-200',
              Awarded: 'bg-success-50 border-success-200',
            };

            return (
              <div
                key={status.status}
                className={`p-4 rounded-xl border ${colors[status.status] || 'bg-muted'}`}
              >
                <div className="text-sm text-muted-foreground mb-1">{status.status}</div>
                <div className="text-2xl font-bold">{status.count}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">{percentage}%</span>
                  <span className="text-sm font-medium">
                    {formatMoneyTotal(status.value, status.currency, status.valueByCurrency)}
                  </span>
                </div>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary/30" style={{ width: `${percentage}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Time to Award */}
      <div className={cn(PANEL, 'p-6')}>
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-muted-foreground" />
          Time to Award Distribution
        </h3>
        <div className="flex items-end gap-2 h-40">
          {data.timeToAwardDistribution.map((item) => {
            const maxCount = Math.max(...data.timeToAwardDistribution.map((d) => d.count));
            const height = (item.count / maxCount) * 100;

            return (
              <div key={item.range} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-primary/20 rounded-t-lg transition-all hover:bg-primary/30"
                  style={{ height: `${height}%` }}
                />
                <div className="mt-2 text-xs text-muted-foreground text-center">{item.range}</div>
                <div className="text-sm font-medium">{item.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-3 gap-6">
        <div className={cn(PANEL, 'p-4')}>
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Award Rate</span>
          </div>
          <div className="text-2xl font-bold">
            {((data.summary.awardedRFQs / data.summary.totalRFQs) * 100).toFixed(1)}%
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {data.summary.awardedRFQs} of {data.summary.totalRFQs} RFQs awarded
          </div>
        </div>

        <div className={cn(PANEL, 'p-4')}>
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Users className="w-4 h-4" />
            <span className="text-sm">Avg. Vendors per RFQ</span>
          </div>
          <div className="text-2xl font-bold">4.2</div>
          <div className="text-xs text-muted-foreground mt-1">Invited carriers average</div>
        </div>

        <div className={cn(PANEL, 'p-4')}>
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Response Rate</span>
          </div>
          <div className="text-2xl font-bold">87.5%</div>
          <div className="text-xs text-muted-foreground mt-1">
            Carriers responding to invitations
          </div>
        </div>
      </div>
    </div>
  );
}

function VendorsTab({ data }: { data: RFQAnalyticsData }) {
  type VendorRow = RFQAnalyticsData['vendorParticipation'][number];

  const vendorColumns: Column<VendorRow>[] = [
    {
      key: 'carrierName',
      header: 'Carrier',
      sortable: true,
      render: (row) => {
        const idx = data.vendorParticipation.indexOf(row);
        return (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-sm font-medium">
              {idx + 1}
            </div>
            <span className="font-medium">{row.carrierName}</span>
          </div>
        );
      },
    },
    { key: 'invitations', header: 'Invitations', sortable: true, className: 'text-right' },
    { key: 'responded', header: 'Responded', sortable: true, className: 'text-right' },
    {
      key: 'responseRate',
      header: 'Response Rate',
      sortable: true,
      className: 'text-right',
      render: (row) => {
        const responseRate = ((row.responded / row.invitations) * 100).toFixed(1);
        return (
          <span
            className={`px-2 py-0.5 rounded text-sm ${
              parseFloat(responseRate) >= 90
                ? 'bg-success-50 text-success-600'
                : parseFloat(responseRate) >= 70
                  ? 'bg-warning-50 text-warning-600'
                  : 'bg-danger-50 text-danger-500'
            }`}
          >
            {responseRate}%
          </span>
        );
      },
    },
    { key: 'awarded', header: 'Awards', sortable: true, className: 'text-right font-medium' },
    {
      key: 'winRate',
      header: 'Win Rate',
      sortable: true,
      className: 'text-right',
      render: (row) => {
        const winRate = ((row.awarded / row.responded) * 100).toFixed(1);
        return (
          <span className="px-2 py-0.5 bg-primary-50 text-primary-600 rounded text-sm">
            {winRate}%
          </span>
        );
      },
    },
    {
      key: 'totalValue',
      header: 'Total Value',
      sortable: true,
      className: 'text-right font-medium',
      render: (row) => formatMoneyTotal(row.totalValue, row.currency, row.valueByCurrency),
    },
  ];

  return (
    <div className="space-y-6">
      <div className={cn(PANEL, 'overflow-hidden')}>
        <div className="px-6 py-4 border-b">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-muted-foreground" />
            Vendor Participation Analysis
          </h3>
        </div>

        <DataTable pagination={false} data={data.vendorParticipation} columns={vendorColumns} />
      </div>

      {/* Visual Comparison */}
      <div className="grid grid-cols-2 gap-6">
        <div className={cn(PANEL, 'p-6')}>
          <h4 className="text-sm font-semibold mb-4">Awards by Carrier</h4>
          <div className="space-y-3">
            {data.vendorParticipation.slice(0, 5).map((vendor) => {
              const maxAwards = Math.max(...data.vendorParticipation.map((v) => v.awarded));
              const width = (vendor.awarded / maxAwards) * 100;

              return (
                <div key={vendor.carrierId}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{vendor.carrierName}</span>
                    <span className="font-medium">{vendor.awarded}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-success-500 rounded-full"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={cn(PANEL, 'p-6')}>
          <h4 className="text-sm font-semibold mb-4">Award Value by Carrier</h4>
          <div className="space-y-3">
            {data.vendorParticipation.slice(0, 5).map((vendor) => {
              // A vendor whose spend spans currencies has no single number to
              // draw a bar from. It gets no bar and prints its breakdown
              // instead — better than a bar drawn from a zero it never was.
              const maxValue = Math.max(...data.vendorParticipation.map((v) => v.totalValue ?? 0));
              const width = vendor.totalValue != null ? (vendor.totalValue / maxValue) * 100 : 0;

              return (
                <div key={vendor.carrierId}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{vendor.carrierName}</span>
                    <span className="font-medium">
                      {formatMoneyTotal(vendor.totalValue, vendor.currency, vendor.valueByCurrency)}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function LanesTab({
  data,
  formatCurrency,
}: {
  data: RFQAnalyticsData;
  formatCurrency: (v: number) => string;
}) {
  type LaneRow = RFQAnalyticsData['topLanes'][number];

  const laneColumns: Column<LaneRow>[] = [
    {
      key: 'lane',
      header: 'Lane',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div>
            <span className="font-medium">{row.originCity}</span>
            <span className="text-muted-foreground text-xs ml-1">({row.originCountry})</span>
          </div>

          <div>
            <span className="font-medium">{row.destCity}</span>
            <span className="text-muted-foreground text-xs ml-1">({row.destCountry})</span>
          </div>
        </div>
      ),
    },
    { key: 'rfqCount', header: 'RFQ Count', sortable: true, className: 'text-right' },
    {
      key: 'totalValue',
      header: 'Total Value',
      sortable: true,
      className: 'text-right font-medium',
      render: (row) => formatMoneyTotal(row.totalValue, row.currency, row.valueByCurrency),
    },
    {
      key: 'avgRate',
      header: 'Avg. Rate',
      sortable: true,
      className: 'text-right',
      render: (row) => formatCurrency(row.avgRate),
    },
    {
      key: 'share',
      header: 'Share',
      className: 'text-right',
      render: (row) => {
        const totalValue = data.topLanes.reduce((sum, l) => sum + (l.totalValue ?? 0), 0);
        const share =
          row.totalValue != null && totalValue > 0
            ? ((row.totalValue / totalValue) * 100).toFixed(1)
            : '0';
        return (
          <div className="flex items-center justify-end gap-2">
            <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: `${share}%` }} />
            </div>
            <span className="text-sm text-muted-foreground">{share}%</span>
          </div>
        );
      },
    },
  ];

  return (
    <div className={cn(PANEL, 'overflow-hidden')}>
      <div className="px-6 py-4 border-b">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-muted-foreground" />
          Top Lanes by RFQ Activity
        </h3>
      </div>

      <DataTable pagination={false} data={data.topLanes} columns={laneColumns} />
    </div>
  );
}

function TrendsTab({ data }: { data: RFQAnalyticsData }) {
  type MonthRow = RFQAnalyticsData['monthlyTrends'][number];

  const monthlyColumns: Column<MonthRow>[] = [
    { key: 'month', header: 'Month', sortable: true, className: 'font-medium' },
    { key: 'rfqCount', header: 'RFQs', sortable: true, className: 'text-right' },
    { key: 'awardedCount', header: 'Awarded', sortable: true, className: 'text-right' },
    {
      key: 'awardRate',
      header: 'Award Rate',
      className: 'text-right',
      render: (row) => {
        const awardRate = ((row.awardedCount / row.rfqCount) * 100).toFixed(1);
        return (
          <span className="px-2 py-0.5 bg-success-50 text-success-600 rounded text-sm">
            {awardRate}%
          </span>
        );
      },
    },
    {
      key: 'totalValue',
      header: 'Total Value',
      sortable: true,
      className: 'text-right font-medium',
      render: (row) => formatMoneyTotal(row.totalValue, row.currency, row.valueByCurrency),
    },
  ];

  const maxCount = Math.max(...data.monthlyTrends.map((m) => m.rfqCount));

  return (
    <div className="space-y-8">
      <div className={cn(PANEL, 'p-6')}>
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-muted-foreground" />
          Monthly RFQ Trends
        </h3>

        {/* Chart */}
        <div className="h-64 flex items-end gap-4">
          {data.monthlyTrends.map((month) => {
            const countHeight = (month.rfqCount / maxCount) * 100;

            return (
              <div key={month.month} className="flex-1 flex flex-col items-center">
                <div className="w-full flex items-end justify-center gap-1 h-48">
                  <div
                    className="w-6 bg-primary-200 rounded-t"
                    style={{ height: `${countHeight}%` }}
                    title={`RFQs: ${month.rfqCount}`}
                  />
                  <div
                    className="w-6 bg-success-200 rounded-t"
                    style={{ height: `${(month.awardedCount / maxCount) * 100}%` }}
                    title={`Awarded: ${month.awardedCount}`}
                  />
                </div>
                <div className="mt-2 text-xs text-muted-foreground text-center">
                  {month.month.split(' ')[0]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-primary-200 rounded" />
            <span className="text-sm text-muted-foreground">Total RFQs</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-success-200 rounded" />
            <span className="text-sm text-muted-foreground">Awarded</span>
          </div>
        </div>
      </div>

      {/* Monthly Stats Table */}
      <div className={cn(PANEL, 'overflow-hidden')}>
        <div className="px-6 py-4 border-b">
          <h4 className="text-sm font-semibold">Monthly Breakdown</h4>
        </div>
        <DataTable pagination={false} data={data.monthlyTrends} columns={monthlyColumns} />
      </div>
    </div>
  );
}
